import assertNever from "assert-never";
import { Instant, LocalDateTime } from "@js-joda/core";
import { Analytics } from "../Analytics";
import { API } from "../API";
import { InternetConnectionError } from "../HttpRequest";
import { isLoading, Loading, loading } from "../Loading";
import * as messages from "../messages";
import {
  TrainingCenterCaregiverPDFWatchData,
  TrainingCenterCaregiverVideoWatchData,
  TrainingCenterDisplayLanguage,
  trainingCenterVideoWatchedPercent,
  VisitBroadcastId,
} from "../messages";
import {
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemId,
  VisitInstanceId,
} from "../messages/ObjectIds";
import {
  CaregiverAssignedVisits,
  CaregiverVisitBroadcast,
  ClockInOutCallType,
  VisitInstanceDetails,
} from "../messages/Visit";
import { genericUpdate } from "../utils/generic_update";
import { AppAgenciesState } from "./AppState/AppAgenciesState";
import { AppCaregiverState } from "./AppState/AppCaregiverState";
import { AppPatientsState } from "./AppState/AppPatientsState";
import { AppScheduledVisitsState } from "./AppState/AppScheduledVisitsState";
import {
  CaregiverTrainingCenterBundles,
  fromCaregiverTrainingCenterBundles,
  fromTrainingCenterCaregiverBundle,
  TrainingCenterBundleItem,
} from "./AppState/AppStateTrainingCenter";
import { AppTaskBroadcastsState } from "./AppState/AppTaskBroadcastssState";
import { AppTasksState, PatientTaskInstance } from "./AppState/AppTasksState";
import { AppVisitEditRequestsState } from "./AppState/AppVisitEditRequestsState";
import { AppVisitsState } from "./AppState/AppVisitsState";
import { ChatState } from "./AppState/ChatState";
import { NotificationState } from "./AppState/NotificationState";
import { EventSource } from "./EventSource";
import { OrderedActionProcessor } from "./OrderedActionProcessor";
import { RealTimeManager } from "./RealTimeManager";

export class AppState {
  constructor(private readonly api: API, private readonly realTimeManager: RealTimeManager) {
    this.realTimeManager.onConnected.register(() => this.handleRealTimeManagerConnected());
    this.realTimeManager.onDisconnected.register(() => this.handleRealTimeManagerDisconnected());

    for (const [eventName, listener] of this.handleRealTimeEvent.entries()) {
      this.realTimeManager.addListener(eventName, listener);
    }

    this.agencyMembersRefresher = new OrderedActionProcessor<messages.CaregiverAgencyMembers>(
      (payload) => this.handleAgencyMembersReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.caregiverRefresher = new OrderedActionProcessor<messages.CaregiverMobile>(
      (payload) => this.handleCaregiverReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.visitsRefresher = new OrderedActionProcessor<messages.CaregiverVisitBroadcastList>(
      (payload) => this.handleVisitsReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.patientTasksRefresher = new OrderedActionProcessor<messages.PatientTaskInstancesList>(
      (payload) => this.handleTasksReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.patientTaskBroadcastssRefresher =
      new OrderedActionProcessor<messages.PatientTaskBroadcastList>(
        (payload) => this.handleTaskBroadcastsReceived(payload),
        (err: any) => {
          if (err instanceof InternetConnectionError) {
            return true;
          } else if (err instanceof API.ServerError) {
            return true;
          }
          console.warn("Unknown network error", err);
          return false;
        }
      );

    this.editRequestRefresher = new OrderedActionProcessor<messages.CaregiverVisitEditRequestList>(
      (payload) => this.handleEditRequestsReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.clockedInStatusRefresher = new OrderedActionProcessor<messages.ClockedInStatus>(
      (payload) => this.handleClockedInStatusReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.scheduledVisitsRefresher = new OrderedActionProcessor<messages.CaregiverAssignedVisits>(
      (payload) => this.handleScheduledVisitsReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    this.trainingCenterRefresher =
      new OrderedActionProcessor<messages.CaregiverTrainingCenterBundles>(
        (payload) => this.handleTrainingCenterBundlesReceived(payload),
        (err: any) => {
          if (err instanceof InternetConnectionError) {
            return true;
          } else if (err instanceof API.ServerError) {
            return true;
          }
          console.warn("Unknown network error", err);
          return false;
        }
      );

    this.patientsRefresher = new OrderedActionProcessor<messages.CaregiverPatientsResponse>(
      (payload) => this.handlePatientsReceived(payload),
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );

    // Load data immediately so that it is visible as early as possible.
    // Not that we must reload all this again after we are connected to real-time system,
    // in order to make sure that we have up-to-date data. But it is good to have
    // this here so that data will be loaded/visible in the app as early as possible.
    this.refreshAgenciesAndAgencyMembers();
    this.refreshCaregiver();
    this.refreshVisits();
    this.refreshSchedule();
    this.refreshPatientTasks();
    this.refreshTaskBroadcasts();
    this.refreshEditRequests();
    this.refreshClockedInStatus();
    this.refreshTrainingCenterBundles();
    this.refreshPatients();

    this.chatState = new ChatState(this.api, async (theApi) => {
      const allChatRooms = await theApi.getAllChatRooms();
      return allChatRooms.chatRooms;
    });

    this.notificationState = new NotificationState(this.api, async (theApi) => {
      return theApi.getNotifications();
    });
  }

  public readonly chatState: ChatState;
  public readonly notificationState: NotificationState;

  private agencyMembersRefresher: OrderedActionProcessor<messages.CaregiverAgencyMembers>;
  private caregiverRefresher: OrderedActionProcessor<messages.CaregiverMobile>;
  private editRequestRefresher: OrderedActionProcessor<messages.CaregiverVisitEditRequestList>;
  private visitsRefresher: OrderedActionProcessor<messages.CaregiverVisitBroadcastList>;
  private patientTasksRefresher: OrderedActionProcessor<messages.PatientTaskInstancesList>;
  private patientTaskBroadcastssRefresher: OrderedActionProcessor<messages.PatientTaskBroadcastList>;
  private clockedInStatusRefresher: OrderedActionProcessor<messages.ClockedInStatus>;
  private scheduledVisitsRefresher: OrderedActionProcessor<messages.CaregiverAssignedVisits>;
  private trainingCenterRefresher: OrderedActionProcessor<messages.CaregiverTrainingCenterBundles>;
  private patientsRefresher: OrderedActionProcessor<messages.CaregiverPatientsResponse>;

  public readonly onAgencyMembersUpdated = new EventSource<void>();
  public readonly onCaregiverUpdated = new EventSource<void>();
  public readonly onVisitsUpdated = new EventSource<void>();
  public readonly onTasksUpdated = new EventSource<void>();
  public readonly onTaskBroadcastsUpdated = new EventSource<void>();
  public readonly onEditRequestUpdated = new EventSource<void>();
  public readonly onClockedInStatusUpdated = new EventSource<void>();
  public readonly onRealTimeConnected = new EventSource<void>();
  public readonly onRealTimeDisconnected = new EventSource<void>();
  public readonly onScheduledVisitsUpdated = new EventSource<void>();
  public readonly onTrainingCenterUpdated = new EventSource<void>();
  public readonly onPatientsUpdated = new EventSource<void>();

  public isRealTimeConnected = false;
  public clockedInStatus: messages.ClockedInStatus | Loading = loading();

  public appCaregiverState: AppCaregiverState | Loading = loading();
  public appAgenciesState: AppAgenciesState | Loading = loading();
  public appVisitsState: AppVisitsState | Loading = loading();
  public appTasksState: AppTasksState | Loading = loading();
  public appTaskBroadcastsState: AppTaskBroadcastsState | Loading = loading();
  public appVisitEditRequestsState: AppVisitEditRequestsState | Loading = loading();
  public scheduledVisits: AppScheduledVisitsState | Loading = loading();
  public closestScheduledShifts: [Instant, Instant][] | Loading = loading();
  public appTrainingCenterState: CaregiverTrainingCenterBundles | Loading = loading();
  public appPatientsState: AppPatientsState | Loading = loading();

  private closed = false;

  public close(): void {
    if (this.closed) {
      throw new Error("AppState already closed");
    }
    this.closed = true;

    this.realTimeManager.close();
    this.agencyMembersRefresher.close();
    this.caregiverRefresher.close();
    this.visitsRefresher.close();
    this.scheduledVisitsRefresher.close();
    this.editRequestRefresher.close();
    this.clockedInStatusRefresher.close();
    this.chatState.close();
    this.notificationState.close();
    this.trainingCenterRefresher.close();
    this.patientsRefresher.close();
  }

  public realTimeDisconnect(): void {
    this.realTimeManager.disconnect();
  }

  public realTimeReconnect(): void {
    this.realTimeManager.reconnect();
  }

  private handleRealTimeManagerConnected(): void {
    this.refreshAgenciesAndAgencyMembers();
    this.refreshCaregiver();
    this.refreshVisits();
    this.refreshSchedule();
    this.refreshPatientTasks();
    this.refreshTaskBroadcasts();
    this.refreshEditRequests();
    this.refreshClockedInStatus();
    this.refreshTrainingCenterBundles();
    this.refreshPatients();
    this.chatState.triggerFullRefresh();
    this.notificationState.triggerNotificationsRefresh();
    this.onRealTimeConnected.broadcast(undefined);
    this.isRealTimeConnected = true;
  }

  private handleRealTimeManagerDisconnected(): void {
    this.onRealTimeDisconnected.broadcast(undefined);
    this.isRealTimeConnected = false;
  }

  private handleRealTimeEvent = new Map<string, (payload: string) => void>([
    [
      "CaregiverUpdated",
      (payload: string) => {
        const parsedPayload =
          API.validateApiResponseMessage<messages.CaregiverUpdatedCaregiverPayload>(
            "CaregiverUpdatedCaregiverPayload",
            payload
          );
        this.refreshCaregiverFromRealTimePush(parsedPayload);
      },
    ],

    [
      "ChatRoomUpdated",
      (payload: string) => {
        const parsedPayload =
          API.validateApiResponseMessage<messages.ChatRoomUpdatedCaregiverPayload>(
            "ChatRoomUpdatedCaregiverPayload",
            payload
          );
        this.chatState.refreshChatFromRealTimePush(parsedPayload);
      },
    ],

    // "AgenciesUpdated" is actually "AgenciesAndAgencyMembersUpdated" (for when caregiver agency association changes)
    ["AgenciesUpdated", () => this.refreshAgenciesAndAgencyMembers()],

    ["VisitsUpdated", () => this.refreshVisits()],

    ["TaskBroadcastsUpdated", () => this.refreshTaskBroadcasts()],
    ["PatientTasksUpdated", () => this.refreshPatientTasks()],
    ["PatientsUpdated", () => this.refreshPatients()],

    // not currently supported
    // ["NewVisitCreated", (payload: string) => {
    //     const parsedPayload = validateApiResponseMessage<messages.NewVisitCreatedCaregiverPayload>("NewVisitCreatedCaregiverPayload", payload);
    //     return this.addNewVisitFromRealTimePush(parsedPayload.visitData);
    // }],

    ["ScheduledVisitsUpdated", () => this.refreshSchedule()],

    ["ScheduledVisitEditRequestChange", () => this.refreshEditRequests()],

    ["ClockedInStatusUpdated", () => this.refreshClockedInStatus()],

    [
      "NotificationsUpdated",
      (payload: string) => {
        const parsedPayload = API.validateApiResponseMessage<messages.PushNotificationInfoList>(
          "PushNotificationInfoList",
          payload
        );
        this.notificationState.addNotificationsFromRealTimePush(parsedPayload);
      },
    ],
  ]);

  handleAgencyMembersReceived(agencyMembers: messages.CaregiverAgencyMembers) {
    if (isLoading(this.appAgenciesState)) {
      this.appAgenciesState = AppAgenciesState.fromMessage(agencyMembers);
    } else {
      this.appAgenciesState = AppAgenciesState.updateFromMessage(
        this.appAgenciesState,
        agencyMembers
      );
    }
    this.onAgencyMembersUpdated.broadcast();
  }

  handleVisitsReceived(visits: messages.CaregiverVisitBroadcastList) {
    if (isLoading(this.appVisitsState)) {
      this.appVisitsState = AppVisitsState.fromMessage(visits);
    } else {
      this.appVisitsState = AppVisitsState.updateFromMessage(this.appVisitsState, visits);
    }

    this.onVisitsUpdated.broadcast(undefined);
  }

  handleTasksReceived(tasks: messages.PatientTaskInstancesList) {
    if (isLoading(this.appTasksState)) {
      this.appTasksState = AppTasksState.fromMessage(tasks);
    } else {
      this.appTasksState = AppTasksState.updateFromMessage(this.appTasksState, tasks);
    }

    this.onTasksUpdated.broadcast();
  }

  handleTaskBroadcastsReceived(taskBroadcasts: messages.PatientTaskBroadcastList) {
    if (isLoading(this.appTaskBroadcastsState)) {
      this.appTaskBroadcastsState = AppTaskBroadcastsState.fromMessage(taskBroadcasts);
    } else {
      this.appTaskBroadcastsState = AppTaskBroadcastsState.updateFromMessage(
        this.appTaskBroadcastsState,
        taskBroadcasts
      );
    }

    this.onTaskBroadcastsUpdated.broadcast();
  }

  handleNewVisitReceived(visit: messages.CaregiverVisitBroadcast) {
    if (!isLoading(this.appVisitsState)) {
      this.appVisitsState = {
        ...this.appVisitsState,
        visits: this.appVisitsState.visits.prepend(AppVisitsState.parseVisit(visit)),
      };
      this.onVisitsUpdated.broadcast(undefined);
    }
  }

  handleEditRequestsReceived(editRequests: messages.CaregiverVisitEditRequestList) {
    if (isLoading(this.appVisitEditRequestsState)) {
      this.appVisitEditRequestsState = AppVisitEditRequestsState.fromMessage(editRequests.requests);
    } else {
      this.appVisitEditRequestsState = AppVisitEditRequestsState.updateFromMessage(
        this.appVisitEditRequestsState,
        editRequests.requests
      );
    }

    this.onEditRequestUpdated.broadcast(undefined);
  }

  handleCaregiverReceived(caregiver: messages.CaregiverMobile) {
    if (isLoading(this.appCaregiverState)) {
      this.appCaregiverState = AppCaregiverState.fromMessage(caregiver);
    } else {
      this.appCaregiverState = AppCaregiverState.updateFromMessage(
        this.appCaregiverState,
        caregiver
      );
    }
    this.onCaregiverUpdated.broadcast(undefined);
  }

  handleCaregiverUpdate(caregiver: Partial<AppCaregiverState["caregiver"]>) {
    if (!isLoading(this.appCaregiverState)) {
      this.appCaregiverState = {
        caregiver: { ...this.appCaregiverState.caregiver, ...caregiver },
      };
      this.onCaregiverUpdated.broadcast(undefined);
    }
  }

  handleClockedInStatusReceived(status: messages.ClockedInStatus) {
    this.clockedInStatus = status;
    this.onClockedInStatusUpdated.broadcast(undefined);
  }

  setVisitInstanceOrTaskVisitInstanceClockInOutTimeByType(
    visitInstanceId: VisitInstanceId,
    type: ClockInOutCallType,
    time: LocalDateTime
  ) {
    switch (type) {
      case ClockInOutCallType.IN:
        return this.setVisitInstanceOrTaskVisitInstanceClockInAndOutTime({
          visitInstanceId,
          clockInTime: time,
          clockOutTime: null,
          timeSheetSubmittedAt: null,
        });
      case ClockInOutCallType.OUT:
        return this.setVisitInstanceOrTaskVisitInstanceClockInAndOutTime({
          visitInstanceId,
          clockInTime: null,
          clockOutTime: time,
          timeSheetSubmittedAt: null,
        });
      default:
        return assertNever(type);
    }
  }

  setVisitInstanceOrTaskVisitInstanceClockInAndOutTime(params: {
    visitInstanceId: VisitInstanceId;
    clockInTime: LocalDateTime | null;
    clockOutTime: LocalDateTime | null;
    timeSheetSubmittedAt: Instant | null;
  }) {
    if (isLoading(this.scheduledVisits) || isLoading(this.appTasksState)) {
      return;
    }

    const scheduledVisitInstance = this.scheduledVisits.scheduledVisitDetails.find(
      (v) => v.id === params.visitInstanceId
    );

    if (scheduledVisitInstance !== undefined) {
      return this.setVisitInstanceClockInAndOutTime({
        scheduledVisitInstance: scheduledVisitInstance,
        clockInTime: params.clockInTime,
        clockOutTime: params.clockOutTime,
        timeSheetSubmittedAt: params.timeSheetSubmittedAt,
      });
    }

    const taskInstance = this.appTasksState.tasks.find(
      (t) => t.type === "SCHEDULED" && t.visitInstance?.id === params.visitInstanceId
    );

    if (taskInstance !== undefined && taskInstance.type === "SCHEDULED") {
      return this.setTaskInstanceClockInAndOutTime({
        taskInstance: taskInstance,
        clockInTime: params.clockInTime,
        clockOutTime: params.clockOutTime,
      });
    }
  }

  setVisitInstanceClockInAndOutTime(params: {
    scheduledVisitInstance: VisitInstanceDetails;
    clockInTime: LocalDateTime | null;
    clockOutTime: LocalDateTime | null;
    timeSheetSubmittedAt: Instant | null;
  }) {
    if (isLoading(this.scheduledVisits)) {
      return;
    }

    const updatedScheduledVisitDetails = this.scheduledVisits.scheduledVisitDetails.mapKeyedList(
      "id",
      (visitInstanceDetails: VisitInstanceDetails): VisitInstanceDetails => {
        if (visitInstanceDetails.id === params.scheduledVisitInstance.id) {
          const $toReturn = { ...visitInstanceDetails };

          if (params.clockInTime !== null) {
            $toReturn.clockInTime = params.clockInTime;
          }

          if (params.clockOutTime !== null) {
            $toReturn.clockOutTime = params.clockOutTime;
          }

          if (params.timeSheetSubmittedAt !== null) {
            $toReturn.timeSheetSubmittedAt = params.timeSheetSubmittedAt;
          }

          return $toReturn;
        } else {
          return visitInstanceDetails;
        }
      }
    );

    this.scheduledVisitsRefresher.enqueue(() => {
      return Promise.resolve({
        visitInstances: updatedScheduledVisitDetails.toArray(),
        importedVisits: [],
      });
    });
  }

  setVisitInstanceDutySheetCompleted = (
    visitInstanceId: VisitInstanceId,
    isDutySheetCompleted: boolean
  ) => {
    if (isLoading(this.scheduledVisits)) {
      return;
    }

    const updatedScheduledVisitDetails = this.scheduledVisits.scheduledVisitDetails.mapKeyedList(
      "id",
      (visitInstanceDetails: VisitInstanceDetails): VisitInstanceDetails => {
        if (visitInstanceDetails.id === visitInstanceId) {
          const $toReturn = { ...visitInstanceDetails };

          $toReturn.isDutySheetCompleted = isDutySheetCompleted;

          return $toReturn;
        } else {
          return visitInstanceDetails;
        }
      }
    );

    this.scheduledVisitsRefresher.enqueue(() => {
      return Promise.resolve({
        visitInstances: updatedScheduledVisitDetails.toArray(),
        importedVisits: [],
      });
    });
  };

  setTaskInstanceClockInAndOutTime(params: {
    taskInstance: PatientTaskInstance.SCHEDULED;
    clockInTime: LocalDateTime | null;
    clockOutTime: LocalDateTime | null;
  }) {
    if (isLoading(this.appTasksState)) {
      return;
    }

    const updatedTasks = this.appTasksState.tasks.mapKeyedList(
      "id",
      (task: PatientTaskInstance): PatientTaskInstance => {
        if (task.type !== "SCHEDULED" || task.id !== params.taskInstance.id) {
          return task;
        }

        const $toReturn = { ...task };

        if ($toReturn.visitInstance !== null) {
          if (params.clockInTime !== null) {
            $toReturn.clockInTime = params.clockInTime;
            $toReturn.visitInstance.clockInTime = params.clockInTime;
          }

          if (params.clockOutTime !== null) {
            $toReturn.clockOutTime = params.clockOutTime;
            $toReturn.visitInstance.clockOutTime = params.clockOutTime;
          }
        }

        return $toReturn;
      }
    );

    this.patientTasksRefresher.enqueue(() => {
      return Promise.resolve({
        tasks: updatedTasks.toArray(),
      });
    });
  }

  handlePatientTaskInstanceReceived(patientTaskInstance: messages.PatientTaskInstance) {
    if (!isLoading(this.appTasksState)) {
      this.appTasksState = {
        tasks: this.appTasksState.tasks.mapKeyedList("id", (task) => {
          if (task.id === patientTaskInstance.id) {
            return patientTaskInstance;
          }
          return task;
        }),
      };

      // TODO @Niv consult with Benny to check why it didn't work. Possibility of mutation?
      // const editRequests: messages.PatientTaskInstancesList = {
      //     tasks: this.appTasksState.tasks.toArray().map(task => {
      //         if (task.id === patientTaskInstance.id) {
      //             return patientTaskInstance;
      //         }
      //         return task;
      //     })
      // };
      // this.appTasksState = AppTasksState.updateFromMessage(this.appTasksState, editRequests);
      this.onTasksUpdated.broadcast();
    }
  }

  filterClosestVisits(visits: [Instant, Instant][]) {
    const now = Instant.now();

    // Filter out all past visits(leave current and future ones).
    const noEndedVisits = visits.filter((visit) => {
      return visit[1].isAfter(now);
    });

    noEndedVisits.sort((a, b) => {
      if (a[0].isBefore(b[0])) {
        return -1;
      }
      if (a[0].isAfter(b[0])) {
        return 1;
      }
      return 0;
    });

    // Save only the 2 first vists, a potential current one, and a future one.
    this.closestScheduledShifts = noEndedVisits.slice(0, 2);
  }

  handleScheduledVisitsReceived(scheduledVisits: messages.CaregiverAssignedVisits) {
    if (isLoading(this.scheduledVisits)) {
      this.scheduledVisits = AppScheduledVisitsState.fromMessage(scheduledVisits);
    } else {
      this.scheduledVisits = AppScheduledVisitsState.updateFromMessage(
        this.scheduledVisits,
        scheduledVisits
      );
    }

    const mappedVisits: [Instant, Instant][] = this.scheduledVisits.scheduledVisitDetails
      .map((scheduledVisitsDetails): [Instant, Instant] => {
        return [scheduledVisitsDetails.startTimeUTC, scheduledVisitsDetails.endTimeUTC];
      })
      .toArray();

    this.filterClosestVisits(mappedVisits);

    this.onScheduledVisitsUpdated.broadcast(undefined);
    // this.onPatientsUpdated.broadcast();
  }

  public incrementScheduledVisitSubmittedAmount(visitInstanceId: VisitInstanceId) {
    if (!isLoading(this.scheduledVisits)) {
      const scheduledVisitInstance = this.scheduledVisits.scheduledVisitDetails.find(
        (v) => v.id === visitInstanceId
      );
      if (scheduledVisitInstance !== undefined) {
        scheduledVisitInstance.visitDocumentsStatus.submitted++;
        const updatedScheduledVisitDetails =
          this.scheduledVisits.scheduledVisitDetails.mapKeyedList(
            "id",
            (visitInstanceDetails: VisitInstanceDetails): VisitInstanceDetails => {
              if (visitInstanceDetails.id === visitInstanceId) {
                return {
                  ...visitInstanceDetails,
                  visitDocumentsStatus: {
                    submitted: visitInstanceDetails.visitDocumentsStatus.submitted++,
                    total: visitInstanceDetails.visitDocumentsStatus.total,
                  },
                };
              } else {
                return visitInstanceDetails;
              }
            }
          );
        const caregiverAssignedVisits: CaregiverAssignedVisits = {
          visitInstances: [],
          importedVisits: [],
        };
        updatedScheduledVisitDetails.toArray().forEach((visit: VisitInstanceDetails) => {
          caregiverAssignedVisits.visitInstances.push(visit);
        });

        this.scheduledVisitsRefresher.enqueue(() => {
          return Promise.resolve(caregiverAssignedVisits);
        });
      }
    }
  }

  handleTrainingCenterBundlesReceived(bundles: messages.CaregiverTrainingCenterBundles): void {
    if (isLoading(this.appTrainingCenterState)) {
      this.appTrainingCenterState = fromCaregiverTrainingCenterBundles(bundles);
    } else {
      this.appTrainingCenterState = genericUpdate(
        this.appTrainingCenterState,
        fromCaregiverTrainingCenterBundles(bundles)
      );
    }

    this.onTrainingCenterUpdated.broadcast();
  }

  public trainingCenterUpdateVideoBundleItemWatchData(
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId,
    language: TrainingCenterDisplayLanguage,
    watchData: TrainingCenterCaregiverVideoWatchData
  ): void {
    if (isLoading(this.appTrainingCenterState)) {
      return;
    }

    {
      const bundle = this.appTrainingCenterState.bundles.find(
        (x) => x.bundleDueDateId === bundleDueDateId
      );
      if (bundle === undefined) {
        return;
      }

      const bundleItem = bundle.bundleItems.find((x) => x.bundleItemId === bundleItemId);
      if (bundleItem === undefined) {
        return;
      }

      if (bundleItem.media.type !== "Video") {
        return;
      }

      const videoInfo = bundleItem.media.videoInfo.find((x) => x.language === language);
      if (videoInfo === undefined) {
        return;
      }

      const requiredWatchTimePercent = bundleItem.media.requiredWatchTimePercent;

      if (
        !bundleItem.testUnlocked &&
        trainingCenterVideoWatchedPercent(videoInfo.data, watchData) >= requiredWatchTimePercent
      ) {
        this.refreshTrainingCenterBundle(bundleDueDateId);
      }
    }

    function updateBundleItemVideoWatchData(
      bundleItem: TrainingCenterBundleItem,
      newWatchData: TrainingCenterCaregiverVideoWatchData
    ): TrainingCenterBundleItem {
      if (bundleItem.media.type !== "Video") {
        return bundleItem;
      }

      if (bundleItem.media.videoWatchData.find((x) => x.language === language) === undefined) {
        return {
          ...bundleItem,
          media: {
            ...bundleItem.media,
            videoWatchData: bundleItem.media.videoWatchData.append({
              language: language,
              data: newWatchData,
            }),
          },
        };
      }

      const newVideoWatchData = bundleItem.media.videoWatchData.findAndModify(
        (x) => x.language === language,
        (videoWatchData) => {
          // There is a possible race condition where we might end
          // up getting an older version of a "watchData" response,
          // so we should only update if the new watchData has more
          // progress than the current
          if (newWatchData.totalSecondsWatched >= videoWatchData.data.totalSecondsWatched) {
            const updatedWatchData = genericUpdate(videoWatchData.data, newWatchData);
            if (updatedWatchData !== videoWatchData.data) {
              return {
                ...videoWatchData,
                data: updatedWatchData,
              };
            } else {
              return videoWatchData;
            }
          } else {
            return videoWatchData;
          }
        }
      );

      if (newVideoWatchData !== bundleItem.media.videoWatchData) {
        return {
          ...bundleItem,
          media: {
            ...bundleItem.media,
            videoWatchData: newVideoWatchData,
          },
        };
      } else {
        return bundleItem;
      }
    }

    const newBundles = this.appTrainingCenterState.bundles.findAndModify(
      (x) => x.bundleDueDateId === bundleDueDateId,
      (bundle) => {
        const newBundleItems = bundle.bundleItems.findAndModify(
          (x) => x.bundleItemId === bundleItemId,
          (bundleItem) => updateBundleItemVideoWatchData(bundleItem, watchData)
        );

        if (newBundleItems !== bundle.bundleItems) {
          return {
            ...bundle,
            bundleItems: newBundleItems,
          };
        } else {
          return bundle;
        }
      }
    );

    if (newBundles !== this.appTrainingCenterState.bundles) {
      this.appTrainingCenterState = {
        ...this.appTrainingCenterState,
        bundles: newBundles,
      };
      this.onTrainingCenterUpdated.broadcast();
    }
  }

  public trainingCenterUpdatePDFBundleItemWatchData(
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId,
    watchData: TrainingCenterCaregiverPDFWatchData
  ): void {
    if (isLoading(this.appTrainingCenterState)) {
      return;
    }

    {
      const bundle = this.appTrainingCenterState.bundles.find(
        (x) => x.bundleDueDateId === bundleDueDateId
      );
      if (bundle === undefined) {
        return;
      }

      const bundleItem = bundle.bundleItems.find((x) => x.bundleItemId === bundleItemId);
      if (bundleItem === undefined) {
        return;
      }

      if (bundleItem.media.type !== "PDF") {
        return;
      }

      const requiredViewTimeMinutes = bundleItem.media.requiredViewTimeMinutes;

      if (
        !bundleItem.testUnlocked &&
        watchData.totalSecondsWatched >= requiredViewTimeMinutes * 60
      ) {
        this.refreshTrainingCenterBundle(bundleDueDateId);
      }
    }

    function updateBundleItemPDFWatchData(
      bundleItem: TrainingCenterBundleItem,
      newWatchData: TrainingCenterCaregiverPDFWatchData
    ): TrainingCenterBundleItem {
      if (bundleItem.media.type !== "PDF") {
        return bundleItem;
      }

      const updatedWatchData = genericUpdate(bundleItem.media.pdfWatchData, newWatchData);

      if (updatedWatchData !== bundleItem.media.pdfWatchData) {
        return {
          ...bundleItem,
          media: {
            ...bundleItem.media,
            pdfWatchData: updatedWatchData,
          },
        };
      } else {
        return bundleItem;
      }
    }

    const newBundles = this.appTrainingCenterState.bundles.findAndModify(
      (bundle) => bundle.bundleDueDateId === bundleDueDateId,
      (bundle) => {
        const newBundleItems = bundle.bundleItems.findAndModify(
          (bundleItem) => bundleItem.bundleItemId === bundleItemId,
          (bundleItem) => updateBundleItemPDFWatchData(bundleItem, watchData)
        );

        if (newBundleItems !== bundle.bundleItems) {
          return {
            ...bundle,
            bundleItems: newBundleItems,
          };
        } else {
          return bundle;
        }
      }
    );

    if (newBundles !== this.appTrainingCenterState.bundles) {
      this.appTrainingCenterState = {
        ...this.appTrainingCenterState,
        bundles: newBundles,
      };
      this.onTrainingCenterUpdated.broadcast();
    }
  }

  handlePatientsReceived(payload: messages.CaregiverPatientsResponse): void {
    if (isLoading(this.appPatientsState)) {
      this.appPatientsState = AppPatientsState.fromMessage(payload);
    } else {
      this.appPatientsState = genericUpdate(
        this.appPatientsState,
        AppPatientsState.fromMessage(payload)
      );
    }

    this.onPatientsUpdated.broadcast();
  }

  public refreshAgenciesAndAgencyMembers(): void {
    this.agencyMembersRefresher.enqueue(() => this.api.getCaregiverAgencyMembers());
  }

  public refreshCaregiver(): void {
    this.caregiverRefresher.enqueue(() => this.api.getCaregiver());
  }

  public refreshCaregiverFromRealTimePush(
    caregiverPayload: messages.CaregiverUpdatedCaregiverPayload
  ): void {
    this.caregiverRefresher.enqueue(() => Promise.resolve(caregiverPayload.caregiverData));
  }

  countVisitsByType(visitBroadcasts: CaregiverVisitBroadcast[]) {
    const result = {
      AVAILABLE: 0,
      REQUESTED: 0,
      UNSEEN: 0,
      UNSEEN_MORE_DETAILS: 0,
    };

    for (const visitBroadcast of visitBroadcasts) {
      result.AVAILABLE++;

      if (!visitBroadcast.caregiverVisitEngagementDetails.viewed) {
        result.UNSEEN++;
      }

      if (!visitBroadcast.caregiverVisitEngagementDetails.moreDetailsViewed) {
        result.UNSEEN_MORE_DETAILS++;
      }

      if (visitBroadcast.caregiverVisitEngagementDetails.requested) {
        result.REQUESTED++;
      }
    }

    return result;
  }
  public refreshPatientTasks(): void {
    this.patientTasksRefresher.enqueue(() => {
      return this.api.getPatientTaskInstances();
    });
  }

  public refreshTaskBroadcasts(): void {
    this.patientTaskBroadcastssRefresher.enqueue(() => {
      return this.api.getPatientTaskBroadcasts();
    });
  }

  public refreshVisits(): void {
    this.visitsRefresher.enqueue(() => {
      return this.api.getCaregiverVisits().then((visits) => {
        Analytics.track("Visits received counters", this.countVisitsByType(visits.visitBroadcasts));
        return visits;
      });
    });
  }

  public refreshSchedule(): void {
    this.scheduledVisitsRefresher.enqueue(() => {
      return this.api.getCaregiverSchedule();
    });
  }

  public refreshEditRequests(): void {
    this.editRequestRefresher.enqueue(() => this.api.getScheduledVisitEditRequests());
  }

  public refreshClockedInStatus(): void {
    this.clockedInStatusRefresher.enqueue(() => this.api.getClockedInStatus());
  }

  public refreshPatients(): void {
    this.patientsRefresher.enqueue(() => this.api.getCaregiverPatients());
  }

  public refreshTrainingCenterBundles(): void {
    this.trainingCenterRefresher.enqueue(() => this.api.getTrainingCenterBundles());
  }

  public refreshTrainingCenterBundle(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId
  ): void {
    // TODO !!! This is horrible. We need to do it the same way as we do
    // in `ChatState.handleChatReceived` (handle races with full refresh,
    // handle error and retries, etc...). I will build a simple generic
    // framework that will allow to use the same code in both places
    this.api.getTrainingCenterBundle(trainingCenterBundleDueDateId).then(
      (bundle) => {
        if (isLoading(this.appTrainingCenterState)) {
          // Uh oh?
          return;
        }

        const newBundles = this.appTrainingCenterState.bundles.findAndModify(
          (b) => b.bundleDueDateId === trainingCenterBundleDueDateId,
          (oldBundle) => genericUpdate(oldBundle, fromTrainingCenterCaregiverBundle(bundle))
        );

        if (newBundles !== this.appTrainingCenterState.bundles) {
          this.appTrainingCenterState = {
            ...this.appTrainingCenterState,
            bundles: newBundles,
          };

          this.onTrainingCenterUpdated.broadcast();
        }
      },
      (err) => {
        console.warn(
          `ERROR refreshing training center bundle: TrainingCenterBundleDueDateId ${trainingCenterBundleDueDateId}`,
          err
        );
      }
    );
  }

  public setVisitMoreDetailsViewed(visitId: VisitBroadcastId): any {
    if (!isLoading(this.appVisitsState)) {
      this.appVisitsState = AppVisitsState.setVisitMoreDetailsViewed(this.appVisitsState, visitId);
      this.onVisitsUpdated.broadcast();
    }
  }
}
